import React, { useState } from "react";
import NavbarWrapper from "../../../widgets/navbarWrapper";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Spinner from "../loading/spinner";

// ============================================
// LOCALSTORAGE HELPER
// ============================================

const getDoctorData = () => {
  try {
    return JSON.parse(localStorage.getItem("user")) || {};
  } catch {
    return {};
  }
};

// ============================================
// MAIN COMPONENT
// ============================================

const Help = () => {
  const doctorData = getDoctorData();
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [formData, setFormData] = useState({
    firstName: doctorData.firstName || "",
    lastName: doctorData.lastName || "",
    emailFrom: doctorData.email || "",
    phoneNumber: doctorData.contact || "",
    content: "",
  });

  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});

  const validate = (values) => {
    const err = {};
    if (!values.content || values.content.trim() === "") {
      err.content = "Please enter your message";
    }
    return err;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const err = validate(formData);
    setErrors(err);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const err = validate(formData);
    setErrors(err);
    setTouched({ content: true });
    if (Object.keys(err).length > 0) {
      toast.error("Please fill all required fields.");
      return;
    }

    setLoadingSpinner(true);
    setTimeout(() => {
      setLoadingSpinner(false);
      toast.success("Thank you! Your feedback has been sent successfully.");
      setFormData((prev) => ({ ...prev, content: "" }));
      setErrors({});
    }, 1500);
  };

  // SVG Icons (scalable)
  const LocationIcon = () => (
    <svg className="w-5 h-5 text-teal-200 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );

  const EmailIcon = () => (
    <svg className="w-5 h-5 text-teal-200 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );

  const PhoneIcon = () => (
    <svg className="w-5 h-5 text-teal-200 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );

  const SendIcon = () => (
    <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
    </svg>
  );

  return (
    <>
      <NavbarWrapper showNavbar={true} />

      <div className="h-[calc(99vh-64px)] overflow-hidden bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center px-4 py-4 sm:py-6">
        <div className="w-full max-w-6xl bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100 h-full max-h-[800px] flex flex-col md:flex-row">
          
          {/* Left Panel - Contact Info (30% width on desktop) */}
          <div className="w-full md:w-1/3 p-6 md:p-8 lg:p-10 bg-gradient-to-br from-teal-600 to-teal-800 text-white flex flex-col justify-between overflow-y-auto">
            <div>
              <div className="flex items-center mb-4">
                <SendIcon />
                <h2 className="text-2xl lg:text-3xl font-bold ml-2">Get In Touch</h2>
              </div>
              <p className="text-teal-100 text-sm lg:text-base mb-6 leading-relaxed">
                Have any questions or feedback? We'd love to hear from you.
              </p>
              <div className="space-y-4 lg:space-y-5">
                <div className="flex items-start">
                  <LocationIcon />
                  <div className="ml-3">
                    <p className="font-semibold text-sm lg:text-base">Address</p>
                    <p className="text-teal-100 text-sm lg:text-base">Lanka, Sankat Mochan, Varanasi</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <EmailIcon />
                  <div className="ml-3">
                    <p className="font-semibold text-sm lg:text-base">Email</p>
                    <a href="mailto:enquiry@doc-aid.in" className="text-teal-100 text-sm lg:text-base hover:text-white transition">
                      enquiry@doc-aid.in
                    </a>
                  </div>
                </div>
                <div className="flex items-start">
                  <PhoneIcon />
                  <div className="ml-3">
                    <p className="font-semibold text-sm lg:text-base">Phone</p>
                    <p className="text-teal-100 text-sm lg:text-base">+91-6387722743</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 pt-4 border-t border-teal-500">
              <p className="text-xs lg:text-sm text-teal-200">We respond within 24 hours.</p>
            </div>
          </div>

          {/* Right Panel - Contact Form (70% width on desktop) */}
          <div className="w-full md:w-2/3 p-6 md:p-8 lg:p-10 bg-white overflow-y-auto">
            <h3 className="text-xl lg:text-2xl font-semibold text-gray-800 mb-5 lg:mb-6">
              Send Us a Message
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4 lg:space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="firstName">
                    First Name
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="w-full px-3 py-2 text-sm lg:text-base rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent transition duration-200"
                    placeholder="John"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="lastName">
                    Last Name
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="w-full px-3 py-2 text-sm lg:text-base rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent transition duration-200"
                    placeholder="Doe"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="emailFrom">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="emailFrom"
                    name="emailFrom"
                    value={formData.emailFrom}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="w-full px-3 py-2 text-sm lg:text-base rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent transition duration-200"
                    placeholder="you@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="phoneNumber">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="w-full px-3 py-2 text-sm lg:text-base rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent transition duration-200"
                    placeholder="+91 98765 43210"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="content">
                  Your Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="content"
                  name="content"
                  rows="4"
                  value={formData.content}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`w-full px-3 py-2 text-sm lg:text-base rounded-lg border ${touched.content && errors.content ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-teal-500 focus:border-transparent transition duration-200`}
                  placeholder="Write your feedback or query here..."
                />
                {touched.content && errors.content && (
                  <p className="text-red-500 text-xs mt-1">{errors.content}</p>
                )}
              </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={loadingSpinner}
                  className="px-6 py-2.5 lg:px-8 lg:py-3 bg-gradient-to-r from-teal-600 to-teal-700 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition duration-200 flex items-center disabled:opacity-50 text-sm lg:text-base"
                >
                  <SendIcon />
                  {loadingSpinner ? "Sending..." : "Send Message"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <ToastContainer />
      {loadingSpinner && <Spinner />}
    </>
  );
};

export default Help;